/**
 * TopSearchV2 — redesigned editor for the "Top Search" list (the
 * pinned-at-top search results shown when the user hasn't typed
 * anything yet).
 *
 * Visual recipe mirrors CancellationReasonPageV2:
 *   • Cream-card row with drag handle, ordinal number, TextInput, trash
 *   • "Add new" appends an unsaved row (no top_search_id yet)
 *   • "Save" submits the whole array to the backend
 *
 * Same `topSearchActions` plumbing as v1:
 *   fetchTopSearches / addOrEditTopSearch / deleteTopSearch.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  fetchTopSearches,
  deleteTopSearch,
  addOrEditTopSearch,
} from '../../../actions/topSearchActions';
import { Button, IconButton, cn } from '../../redesign';
import { TextInput } from '../../redesign/edit';
import { toast } from '../../../utils/v2Toast';

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const PlusSign = (
  <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>+</span>
);

const DragHandleIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 13, height: 13 }}>
    <circle cx="9"  cy="6"  r="0.5" />
    <circle cx="9"  cy="12" r="0.5" />
    <circle cx="9"  cy="18" r="0.5" />
    <circle cx="15" cy="6"  r="0.5" />
    <circle cx="15" cy="12" r="0.5" />
    <circle cx="15" cy="18" r="0.5" />
  </svg>
);

const TrashIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

/* Stable id helper. New (unsaved) rows have no top_search_id yet,
 * so we mint a `new-…` string that's stable across renders until
 * the save round-trip replaces the row with the persisted one. */
function rowId(item) {
  return item.top_search_id ?? item._tempId;
}

function SortableRow({ item, index, onChange, onDelete }) {
  const id = rowId(item);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="bg-surface border border-sand flex items-center"
      data-dragging={isDragging ? 'true' : 'false'}
    >
      <span
        {...listeners}
        aria-label="Drag to reorder"
        tabIndex={0}
        className={cn(
          'grid place-items-center text-text-muted shrink-0 cursor-grab',
          'hover:text-navy active:cursor-grabbing',
          'focus:outline-none focus-visible:text-navy',
        )}
        style={{ width: 36, height: 48, touchAction: 'none' }}
      >
        {DragHandleIcon}
      </span>
      <span
        className="font-mono text-text-muted text-center shrink-0"
        style={{ width: 32, fontSize: 11.5 }}
      >
        {index + 1}
      </span>
      <div style={{ flex: 1, minWidth: 0, padding: '8px 8px 8px 0' }}>
        <TextInput
          value={item.value || ''}
          onChange={(e) => onChange(index, e.target.value)}
          placeholder="Top search text"
        />
      </div>
      <span style={{ paddingRight: 8 }}>
        <IconButton
          size="sm"
          aria-label="Remove"
          title="Remove"
          onClick={(e) => onDelete(e, item.top_search_id, index)}
          type="button"
        >
          {TrashIconSvg}
        </IconButton>
      </span>
    </div>
  );
}

export default function TopSearchV2() {
  const dispatch = useDispatch();
  const topSearchFromStore = useSelector(
    (state) => state.search.top_search_data,
  );

  const [items, setItems] = useState([]);

  // Initial fetch on mount.
  useEffect(() => {
    dispatch(fetchTopSearches());
  }, [dispatch]);

  // Mirror Redux into local state — but only assign a _tempId once
  // (when we receive the slice from the store) so existing rows
  // keep stable dnd-kit ids across re-renders.
  useEffect(() => {
    if (Array.isArray(topSearchFromStore)) {
      setItems(
        topSearchFromStore.map((row) => ({
          ...row,
          _tempId: row.top_search_id ? undefined : `new-${Math.random().toString(36).slice(2)}`,
        })),
      );
    }
  }, [topSearchFromStore]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      const oldIndex = prev.findIndex((it) => rowId(it) === active.id);
      const newIndex = prev.findIndex((it) => rowId(it) === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleChange = useCallback((index, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], value };
      return next;
    });
  }, []);

  const handleDelete = useCallback(
    async (e, top_search_id, index) => {
      e?.preventDefault?.();
      if (top_search_id) {
        await dispatch(deleteTopSearch(top_search_id));
      }
      setItems((prev) => prev.filter((_, i) => i !== index));
    },
    [dispatch],
  );

  const handleAdd = useCallback(() => {
    setItems((prev) => {
      const hasUnsaved = prev.some((it) => it.top_search_id === undefined);
      if (hasUnsaved) {
        toast.error('Please save the current new item before adding another.');
        return prev;
      }
      return [
        ...prev,
        {
          value: '',
          sort_order: prev.length + 1,
          _tempId: `new-${Math.random().toString(36).slice(2)}`,
        },
      ];
    });
  }, []);

  const handleSave = useCallback(async () => {
    const invalid = items.find((it) => !it.value);
    if (invalid) {
      toast.error('Please fill all missing data for top search.');
      return;
    }
    // Re-stamp sort_order from the current row order, then strip the
    // local-only _tempId before sending to the backend.
    const payload = items.map((it, idx) => {
      const { _tempId, ...rest } = it;
      return { ...rest, sort_order: idx + 1 };
    });
    try {
      await dispatch(addOrEditTopSearch(JSON.stringify(payload)));
      toast.success('Top searches updated!');
    } catch (_err) {
      toast.error('Failed to update top searches. Please try again.');
    }
  }, [dispatch, items]);

  return (
    <div>
      <div className="text-text-muted" style={{ fontSize: 11.5, margin: '0 4px 8px' }}>
        Drag any row to reorder
      </div>

      {items.length === 0 ? (
        <div
          className="bg-cream border border-sand text-text-muted italic text-center"
          style={{ borderRadius: 10, padding: '32px 16px', fontSize: 13 }}
        >
          No top searches yet — click "Add new" to create one.
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map(rowId)}
            strategy={verticalListSortingStrategy}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {items.map((it, idx) => (
                <SortableRow
                  key={rowId(it)}
                  item={it}
                  index={idx}
                  onChange={handleChange}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <div
        className="flex items-center"
        style={{ gap: 8, marginTop: 16, justifyContent: 'space-between' }}
      >
        <Button
          variant="ghost"
          leadingIcon={PlusSign}
          onClick={handleAdd}
          disabled={items.some((it) => it.top_search_id === undefined && !it.value)}
        >
          Add new
        </Button>
        <Button variant="coral" leadingIcon={SaveIconSvg} onClick={handleSave}>
          Save
        </Button>
      </div>
    </div>
  );
}
